import * as React from 'react';
import * as cx from 'classnames';
import { noDefault } from 'lib/eventWrappers';
import SuperCritical from 'lib/supercritical';
import TZDate from 'lib/TZDate';
import { tzmoment } from 'lib/tzmoment';

import { Event } from 'data/models/Event';
import { TopicCardAvailableAttachment } from 'data/models/TopicCardAvailableAttachment';
import { TopicCardChangeType } from 'server/enums/TopicCardChangeType';
import { TopicCardOption } from 'data/models/TopicCardOption';
import { TopicCardQuestionType } from 'server/enums/TopicCardQuestionType';
import { TopicCardStatus } from 'server/enums/TopicCardStatus';
import { TopicCardTemplateAttachment } from 'data/models/TopicCardTemplateAttachment';
import { TopicCardTemplateOption } from 'data/models/TopicCardTemplateOption';
import { TopicCardTemplateUserAssignment } from 'data/models/TopicCardTemplateUserAssignment';
import { TopicCardUserAssignment } from 'data/models/TopicCardUserAssignment';
import {
    TopicCards,
    TopicCardOrTemplate,
    CardLogEntry,
    CommentModel,
    ChangeModel,
    UserModel,
    UserAssignmentModel
} from './model';

import { ActionsMenuButton } from 'portcall/actionsmenubutton/view';
import { NullableDateTimePicker } from 'portcall/datetimepicker/view';
import { DownloadLink } from 'portcall/downloadlink/view';
import { PCAutoScroll } from 'portcall/pcautoscroll/view';
import { PCDismissible } from 'portcall/pcdismissible/view';
import { TimeAgoTooltip } from 'portcall/pctooltip/view';
import { PersonProfilePopupView } from 'portcall/personprofilepopup/view';
import { PortCallHeaderView } from 'portcall/portcallprofile/portcallheaderview';
import { ToggleButton } from 'portcall/togglebutton/view';
import { TopicCardListItemView } from 'portcall/topiccardlistitem/view';
import { UploadFileView } from 'portcall/uploadfile/view';

export const TopicCardsView = SuperCritical(({ model: m } : { model : TopicCards}) =>
    <div className="topiccards pc-page-fixedwidth">
        <div className="pc-actions-links-header">
            <a className="pc-back-link" href="" onClick={noDefault(m.GoBack)}>&lt;&lt; Back</a>
            <ActionsMenuButton onClick={m.ToggleActionsMenu} />
            <ActionsMenu model={m} />
        </div>
        <PortCallHeaderView portcall={m.PortCall} canEdit={m.Model.CanEditPortCall} />
        <PageBody model={m} />
    </div>
);

let PageBody = SuperCritical(({ model: m } : { model : TopicCards }) =>
    <div className="pc-card page-body">
        <div className="pc-card-header pc-card-title">Cards</div>
        <CardList model={m} />
        <SelectedCardView model={m} />
    </div>
);

let CardList = SuperCritical(({ model: m } : { model : TopicCards }) =>
    <div className="card-list">
        <CardSublist model={m} cards={m.AnsweredCards} title="Answered" />
        <CardSublist model={m} cards={m.DefaultCards}  title="Default" />
    </div>
);

let CardSublist = SuperCritical(({ model: m, cards, title } : { model : TopicCards, cards : () => TopicCardOrTemplate[], title : string }) =>
    <div className="card-sublist">
        <div className="card-sublist-header pc-card-title">{title}</div>
        {cards().map(card =>
            <CardListItem model={m} card={card} key={card.ReactKey} />
        )}
    </div>
);

let CardListItem = SuperCritical(({ model: m, card } : { model : TopicCards, card : TopicCardOrTemplate }) =>
    <PCAutoScroll keepInView={() => m.SelectedCard() === card}
        scrollParent={scrollParent}
        retriggerScroll={() => { m.AnsweredCards(); m.DefaultCards(); } /* Re-trigger if either sublist changes */}>
        <TopicCardListItemView className={cx({ selected: m.SelectedCard() === card })}
            model={card.ListItem}
            hideLastUpdate={() => !card.IsAnswered()}
            onClick={() => m.SelectedCardKey(card.SelectionKey())} />
    </PCAutoScroll>
);

function scrollParent(el : HTMLElement) {
    let parent = el.parentElement;
    while (parent && !parent.classList.contains('card-list')) {
        parent = parent.parentElement;
    }

    return parent;
}

let SelectedCardView = SuperCritical(({ model: m } : { model : TopicCards }) => {
    const c = m.SelectedCard();
    if (!c) return null;
    return (
        <div className="selected-card-edit">
            <CardTitle card={c} />
            <CardBody model={m} card={c} />
        </div>
    );
});

let CardTitle = SuperCritical(({ card: c } : { card : TopicCardOrTemplate }) =>
    <div className="selected-card-title card-section-header">{c.Model.Title()}</div>
);

let CardBody = SuperCritical(({ model: m, card: c } : { model: TopicCards, card : TopicCardOrTemplate }) =>
    <div className="selected-card-body">
        <div className="selected-card-particulars">
            <CardQuestionAndAnswer card={c} />
            <CardTimeEntry model={m} card={c} />
            <CardPeople card={c} />
            <CardAvailableAttachments card={c} />
            <CardRemarks card={c} />
        </div>
        <CardLog model={m} card={c} />
    </div>
);

let CardQuestionAndAnswer = SuperCritical(({ card: c } : { card : TopicCardOrTemplate }) =>
    <div className="card-section card-question-and-answer">
        <div className="card-section-body">
            <div>{c.Model.Question()}</div>
            <CardAnswerView card={c} />
        </div>
    </div>
);

let CardAnswerView = SuperCritical(({ card: c } : { card : TopicCardOrTemplate }) =>
    <div className="card-answer">
        {c.Model.QuestionType() === TopicCardQuestionType.YesNo &&
            <ToggleButton checked={c.Model.IsYes} onLabel="Yes" offLabel="No" disabled={!c.CanEdit()} />
        }
        {c.Model.QuestionType() === TopicCardQuestionType.MultipleChoice && c.Options().map(o =>
            <CardOptionView card={c} option={o} key={o.Id()} />
        )}
    </div>
);

let CardOptionView = SuperCritical(({ card: c, option: o } : { card : TopicCardOrTemplate, option : TopicCardOption | TopicCardTemplateOption }) => {
    const selected = c.SelectedOption();
    if (!selected) return null;
    return (
        <div className="card-option-row">
            <label className="pc-radio pc-text-tinylabel">
                <input type="radio"
                    checked={selected.OptionId() === o.Id()}
                    onChange={e => e.currentTarget.checked && selected.OptionId(o.Id())}
                    disabled={!c.CanEdit()} />
                <span className="pc-radio-button"></span>
                {' '}
                <span className="pc-text-tinylabel">{o.Text()}</span>
            </label>
        </div>
    );
});

let CardTimeEntry = SuperCritical(({ model: m, card: c } : { model : TopicCards, card : TopicCardOrTemplate }) => {
    if (!c.Model.TimeEntry.HasTimeEntry()) return null;
    return (
        <div className="card-section card-time-entry">
            <div className="card-section-header">{c.Model.TimeEntry.EffectiveLabel()}</div>
            <div className="card-section-body">
                <NullableDateTimePicker
                    value={c.Model.TimeEntry.Response}
                    disabled={!c.CanEdit()}
                    localization={m.Localization}
                />
            </div>
        </div>
    );
});

let CardPeople = SuperCritical(({ card: c } : { card : TopicCardOrTemplate }) =>
    <div className="card-section card-people">
        <div className="card-section-header">People</div>
        <div className="card-section-body">
            {c.People().map(u =>
                <UserAssignmentView userAssignment={u} key={u.Model.UserId()} />
            )}
        </div>
    </div>
);

let UserAssignmentView = SuperCritical(({ userAssignment : u } : { userAssignment : UserAssignmentModel }) =>
    <div className="user-assignment-row">
        <UserView user={u.User} />
    </div>
);

let CardAvailableAttachments = SuperCritical(({ card: c } : { card : TopicCardOrTemplate }) =>
    <div className="card-section card-attachments">
        <div className="card-section-header">Attachments</div>
        <div className="card-section-body">
            {c.AvailableAttachments().map(a =>
                <CardAttachmentView attachment={a} canViewAttachments={true} key={a.Id()} />
            )}
        </div>
    </div>
);

let CardAttachmentView = SuperCritical(({ attachment: a, canViewAttachments } : { attachment : TopicCardAvailableAttachment | TopicCardTemplateAttachment, canViewAttachments : boolean }) =>
    <AttachmentOrCommentAttachment url={a.AttachmentUrl()} filename={a.FileName()} canViewAttachments={canViewAttachments} />
);

let CardRemarks = SuperCritical(({ card: c } : { card : TopicCardOrTemplate }) =>
    <div className="card-section card-remarks">
        <div className="card-section-header">Remarks</div>
        <div className="card-section-body">{c.Model.Remarks()}</div>
    </div>
);

let CardLog = SuperCritical(({ model: m, card: c } : { model : TopicCards, card : TopicCardOrTemplate }) =>
    <div className="card-log">
        <NewCommentView card={c} />
        <div className="card-log-list">
            {c.IsAnswered() && c.Log.LogEntries().map(e =>
                <CardLogEntryView model={m} entry={e} card={c} key={e.Model.EventId()} />
            )}
        </div>
    </div>
);

let NewCommentView = SuperCritical(({ card: c } : { card : TopicCardOrTemplate }) => {
    if (!c.CanComment()) return null;
    return (
        <div className="new-comment">
            <div>
                <textarea className="new-comment-input"
                    value={c.Log.NewComment().Message() || ''}
                    onChange={e => c.Log.NewComment().Message(e.currentTarget.value)}
                    placeholder="Add a Comment"
                    rows={2} />
            </div>
            <div className="new-comment-controls">
                {c.Log.NewComment().AttachmentUrl() ?
                    <span className="current-attachment">
                        <span className="attachment-filename pc-text-fixedwidth">
                            {c.Log.NewComment().AttachmentFileName()}
                        </span>
                        <a href="" className="remove-attachment pc-close-icon"
                            onClick={noDefault(() => c.Log.NewComment().AttachmentUrl(null))}></a>
                    </span>
                  : <span className="add-attachment">
                        <label className="add-attachment-link"
                            htmlFor={c.Log.UploadFile.FileInputId()}>
                            <a className="fa fa-paperclip"></a>
                        </label>
                        <UploadFileView model={c.Log.UploadFile} />
                    </span>
                }
                <button className="add-comment-button pc-button"
                    onClick={c.Log.AddComment}
                    disabled={!c.Log.NewComment().Message() && !c.Log.NewComment().AttachmentUrl()}>
                    Add
                </button>
            </div>
        </div>
    );
});

let CardLogEntryView = SuperCritical(({ model: m, entry: e, card: c } : { model : TopicCards, entry : CardLogEntry, card : TopicCardOrTemplate }) =>
    <div className="card-log-entry">
        {e.Type === 'comment' ?
            <CommentView model={m} comment={e} card={c} />
          : <ChangeView model={m} change={e} card={c} />
        }
    </div>
);

let CommentView = SuperCritical(({ model: m, comment: c, card: ca } : { model : TopicCards, comment : CommentModel, card : TopicCardOrTemplate }) =>
    <div>
        <UserAndTimestamp model={m} user={c.Commenter} hasUser={() => true} event={c.Model.Event} />
        <div>{c.Model.Message()}</div>
        {c.Model.AttachmentUrl() &&
            <AttachmentOrCommentAttachment url={c.Model.AttachmentUrl()!} filename={c.Model.AttachmentFileName()!} canViewAttachments={ca.CanViewAttachments()} />
        }
    </div>
);

let ChangeView = SuperCritical(({ model: m, change: c, card } : { model : TopicCards, change : ChangeModel, card : TopicCardOrTemplate }) =>
    <div>
        <UserAndTimestamp model={m} user={c.Changer} hasUser={() => !!(c.Model.Event() && c.Model.Event()!.UserId())} event={c.Model.Event} />
        <div>
            {
                c.Model.Type() === TopicCardChangeType.Creation ?
                    CreationChangeText(c, card)
              : c.Model.Type() === TopicCardChangeType.YesNoAnswer ?
                    YesNoAnswerChangeText(c.Model.NewIsYes)
              : c.Model.Type() === TopicCardChangeType.TimeEntry ?
                    TimeEntryChangeText(c.Model.NewTimeEntryResponse, card)
              : c.Model.Type() === TopicCardChangeType.Status ?
                    StatusChangeText(c)
              : c.Model.Type() === TopicCardChangeType.SelectedOption ?
                    SelectedOptionChangeText(c.Model.NewSelectedOption)
              : null
            }
        </div>
    </div>
);

let UserAndTimestamp = SuperCritical(({ model: m, user, hasUser, event } : { model : TopicCards, user : () => UserModel | null, hasUser : () => boolean, event : () => Event | null }) =>
    <div className="user-and-timestamp">
        <span className="user-name">
            {hasUser() ?
                <UserView user={user} />
              : <span className="domain-name">{m.Domain.DisplayableUrl()}</span>
            }
        </span>
        {event() &&
            <TimeAgoTooltip className="timestamp pc-text-smalllabel pc-text-fixedwidth pc-text-fixedwidth-tooltip"
                date={event()!.Date}
                now={m.Now}
                useAbbreviatedTimeAgo={true} />
        }
    </div>
);

let UserView = SuperCritical(({ user } : { user : () => UserModel | null }) =>
    user() &&
        <span className="user-view">
            <a href="" className="user-name pc-text-fixedwidth" onClick={noDefault(user()!.ToggleProfile)}>{user()!.Model.FullName()}</a>
            <PersonProfilePopupView modelContainer={user()!.ProfilePopup} /* TODO -- Bounding container */ />
        </span>
);

let AttachmentOrCommentAttachment = SuperCritical(({ url, filename, canViewAttachments } : { url : string, filename : string, canViewAttachments : boolean, }) =>
    <div className="card-attachment-row">
        {canViewAttachments ? 
            <div>
                <DownloadLink href={url} className="attachment-icon fa fa-paperclip"></DownloadLink>
                <DownloadLink href={url} className="attachment-filename pc-text-fixedwidth">{filename}</DownloadLink>
            </div>
        :
            <div className={"pc-text-fixedwidth"}>
                {filename}
            </div>
        }
    </div>
);

let ActionsMenu = SuperCritical(({ model : m } : { model : TopicCards }) => m.ShowActionsMenu() ?
    <div className="pc-actions">
        <PCDismissible className="pc-actions-menu pc-card" onDismiss={() =>m.ShowActionsMenu(false)}>
            <div className="action-item">
                <div>
                    <a href={"#!portcallprofile?id=" + m.PortCall.Id()}>View PortCall Profile</a>
                </div>
                <div className="pc-action-item-descr">
                    View this PortCall's Profile.
                </div>
            </div>
            {m.Model.CanManageServices() &&
                <div className="action-item">
                    <div>
                        {/* DC 08/09/2017 -- TODO: Add the date for the PC -> use the PC's First Line Date for the &date=${} section */}
                        <a href={`#!berthmgrdashboard?id=${m.Model.GetOrgId()}&tab=1`} hidden={false}>View on Services Dashboard</a>
                    </div>
                    <div className="pc-action-item-descr">
                        View the services requested at your piers and jetties.
                    </div>
                </div>
            }
        </PCDismissible>
    </div> : null
);

function CreationChangeText(c : ChangeModel, card : TopicCardOrTemplate) {
    return (
        c.Model.OriginalCommentEventId() ? `Answered card with comment.`
      : c.Model.OriginalTimeEntryResponse() ? TimeEntryChangeText(c.Model.OriginalTimeEntryResponse, card)
      : card.Model.QuestionType() === TopicCardQuestionType.YesNo ? YesNoAnswerChangeText(c.Model.OriginalIsYes)
      : card.Model.QuestionType() === TopicCardQuestionType.MultipleChoice ? SelectedOptionChangeText(c.Model.OriginalSelectedOption)
      : 'Answered card.'
    );
}

function YesNoAnswerChangeText(newIsYes : () => boolean) {
    return `Updated card to ${newIsYes() ? 'Yes' : 'No'}.`;
}

function TimeEntryChangeText(newTimeEntryResponse : () => TZDate | null, card : TopicCardOrTemplate) {
    return newTimeEntryResponse() ?
        `Updated ${card.Model.TimeEntry.EffectiveLabel()} to ${tzmoment(newTimeEntryResponse()!).format('MM/DD/YYYY [at] HH:mm')}.`
        : `Removed ${card.Model.TimeEntry.EffectiveLabel()}.`;
}

function StatusChangeText(c : ChangeModel) {
    return (
        c.Model.NewStatus() === TopicCardStatus.Confirmed ? 'Confirmed this service.'
      : c.Model.NewStatus() === TopicCardStatus.Denied ? 'Denied this service.'
      : c.Model.NewStatus() === TopicCardStatus.PendingReview ?
            c.Model.Event() && c.Model.Event()!.UserId() ?
                `Withdrew service ${c.Model.OldStatus() === TopicCardStatus.Confirmed ? 'confirmation' : 'denial'}.`
              : 'Reset status to pending.'
      : ''
    );
}

function SelectedOptionChangeText(newSelectedOption : () => TopicCardOption | null) {
    return `Updated card to ${newSelectedOption() && newSelectedOption()!.Text()}.`;
}


// WEBPACK FOOTER //
// ./apps/portcall/topiccards/view.tsx